import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  MessageSquare,
  X,
  Send,
  RotateCw,
  ArrowUpRight,
  Trash2,
  User,
  Mail,
  Phone,
} from 'lucide-react';
import { ChatMessage, ChatAction, Currency } from '../types';

interface LiveAIAssistantWidgetProps {
  onOpenBooking: () => void;
  onOpenAudit?: (domain?: string) => void;
  onNavigateToPackages?: () => void;
  onNavigateToServices?: () => void;
  onNavigateToContact?: () => void;
  onNavigateToFaq?: () => void;
  currency?: Currency;
  currentView?: string;
}

interface ChatVisitorProfile {
  name: string;
  email: string;
  phone?: string;
}

const VISITOR_STORAGE_KEY = 'xweba_chat_visitor_v1';
const MESSAGES_STORAGE_KEY = 'xweba_concierge_messages_v3';

function createWelcomeMessages(visitorName?: string): ChatMessage[] {
  const firstName = visitorName?.trim().split(/\s+/)[0];
  const greeting = firstName ? `Hi **${firstName}**! Welcome to **Xweba**.` : 'Hi! Welcome to **Xweba**.';
  return [
    {
      id: 'welcome-1',
      role: 'assistant',
      content: `${greeting} Ask me anything about AI-ready websites, Generative Engine Optimization (GEO), or our packages — or jump straight to a page below.`,
      timestamp: 'Just now',
      suggestedFollowUps: [
        'What is GEO vs SEO?',
        'Compare WaaS packages',
        'Audit my website',
      ],
    },
  ];
}

export const LiveAIAssistantWidget: React.FC<LiveAIAssistantWidgetProps> = ({
  onOpenBooking,
  onOpenAudit,
  onNavigateToPackages,
  onNavigateToServices,
  onNavigateToContact,
  currency = 'USD',
  currentView = 'home',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);

  // Pre-chat visitor state (Name, Email, optional Phone)
  const [visitor, setVisitor] = useState<ChatVisitorProfile | null>(() => {
    try {
      const saved = localStorage.getItem(VISITOR_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed.name === 'string' && typeof parsed.email === 'string' && parsed.name.trim() && parsed.email.trim()) {
          return parsed;
        }
      }
    } catch {}
    return null;
  });

  const [nameInput, setNameInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [phoneInput, setPhoneInput] = useState('');
  const [formError, setFormError] = useState('');

  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    try {
      const saved = sessionStorage.getItem(MESSAGES_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
    } catch {}
    return createWelcomeMessages(visitor?.name);
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      sessionStorage.setItem(MESSAGES_STORAGE_KEY, JSON.stringify(messages));
    } catch {}
    if (isOpen && visitor) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen, visitor]);

  useEffect(() => {
    const handleOpenConcierge = (e: Event) => {
      const customEvent = e as CustomEvent<{ prompt?: string }>;
      setIsOpen(true);
      const promptText = customEvent.detail?.prompt;
      if (promptText) {
        if (visitor) {
          handleSendMessage(promptText, visitor);
        } else {
          setPendingPrompt(promptText);
        }
      }
    };
    window.addEventListener('xweba:open-concierge', handleOpenConcierge);
    return () => window.removeEventListener('xweba:open-concierge', handleOpenConcierge);
  }, [visitor]);

  const navigateWithFallback = (path: string, callback?: () => void) => {
    setIsOpen(false);
    if (callback) {
      callback();
      return;
    }
    try {
      window.history.pushState(null, '', path);
      window.dispatchEvent(new PopStateEvent('popstate'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      window.location.href = path;
    }
  };

  const handleActionClick = (action: ChatAction) => {
    if (action.type === 'audit') {
      if (onOpenAudit) {
        setIsOpen(false);
        onOpenAudit(action.domain);
      } else {
        navigateWithFallback('/xwebaudit');
      }
    } else if (action.type === 'packages') {
      navigateWithFallback('/packages', onNavigateToPackages);
    } else if (action.type === 'services') {
      navigateWithFallback('/services', onNavigateToServices);
    } else if (action.type === 'callback') {
      navigateWithFallback('/contact', onNavigateToContact);
    } else if (action.type === 'whatsapp') {
      window.open(
        'https://wa.me/254725724175?text=Hello%20Xweba%20Team%2C%20I%20would%20like%20to%20discuss%20our%20website%20and%20GEO%20growth.',
        '_blank',
        'noopener,noreferrer'
      );
    } else {
      setIsOpen(false);
      onOpenBooking();
    }
  };

  const handleStartChatSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanName = nameInput.trim();
    const cleanEmail = emailInput.trim();
    const cleanPhone = phoneInput.trim();

    if (!cleanName) {
      setFormError('Please enter your name.');
      return;
    }
    if (!cleanEmail || !cleanEmail.includes('@') || !cleanEmail.includes('.')) {
      setFormError('Please enter a valid email address.');
      return;
    }

    setFormError('');
    const newVisitor: ChatVisitorProfile = {
      name: cleanName,
      email: cleanEmail,
      ...(cleanPhone ? { phone: cleanPhone } : {}),
    };

    setVisitor(newVisitor);
    try {
      localStorage.setItem(VISITOR_STORAGE_KEY, JSON.stringify(newVisitor));
    } catch {}

    const initialWelcome = createWelcomeMessages(newVisitor.name);
    setMessages(initialWelcome);

    // Register contact immediately in the backend CRM / AI Concierge folder
    try {
      fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          registerOnly: true,
          visitor: newVisitor,
          messages: [],
          currency,
          currentPage: currentView,
        }),
      }).catch(() => {});
    } catch {}

    if (pendingPrompt) {
      const promptToRun = pendingPrompt;
      setPendingPrompt(null);
      handleSendMessage(promptToRun, newVisitor, initialWelcome);
    }
  };

  const handleResetChat = () => {
    const freshMessages = createWelcomeMessages(visitor?.name);
    setMessages(freshMessages);
    try {
      sessionStorage.removeItem(MESSAGES_STORAGE_KEY);
    } catch {}
  };

  const handleEditVisitorInfo = () => {
    if (visitor) {
      setNameInput(visitor.name);
      setEmailInput(visitor.email);
      setPhoneInput(visitor.phone || '');
    }
    setVisitor(null);
    try {
      localStorage.removeItem(VISITOR_STORAGE_KEY);
    } catch {}
  };

  const handleSendMessage = async (
    textToSend?: string,
    activeVisitor: ChatVisitorProfile | null = visitor,
    baseMessages: ChatMessage[] = messages
  ) => {
    const text = (textToSend ?? input).trim();
    if (!text || isLoading) return;

    const nowTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: nowTime,
    };

    const updatedHistory = [...baseMessages, userMsg];
    setMessages(updatedHistory);
    if (!textToSend) setInput('');
    setIsLoading(true);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitor: activeVisitor,
          messages: updatedHistory.map((m) => ({
            role: m.role,
            content: m.content,
          })),
          currency,
          currentPage: currentView,
        }),
      });

      const data = await response.json();
      if (data.success && data.reply) {
        setMessages((prev) => [
          ...prev,
          {
            id: `assistant-${Date.now()}`,
            role: 'assistant',
            content: data.reply,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            recommendedAction: data.recommendedAction,
            suggestedFollowUps: data.suggestedFollowUps,
          },
        ]);
      } else {
        throw new Error(data.error || 'Chat fallback');
      }
    } catch {
      const lower = text.toLowerCase();
      let fallbackText =
        'We build AI-ready websites engineered for human conversion and AI search citation. You can explore our **Services**, compare **Packages**, or run a **Free Xwebaudit** on your domain.';
      let fallbackAction: ChatAction = { type: 'services', label: 'View Services Page' };

      if (lower.includes('audit') || lower.includes('scan') || lower.includes('geo') || lower.includes('seo') || lower.includes('llms')) {
        fallbackText =
          '**GEO (Generative Engine Optimization)** structures your website with Schema.org entity graphs and `/llms.txt` files so ChatGPT, Perplexity, and Google AI Overviews cite your business.';
        fallbackAction = { type: 'audit', label: 'Go to Free Xwebaudit Page' };
      } else if (lower.includes('price') || lower.includes('package') || lower.includes('cost') || lower.includes('waas') || lower.includes('kes') || lower.includes('usd')) {
        fallbackText =
          'We offer 4 transparent tiers (**AI Starter, AI Growth, AI Pro, and AI Enterprise**) available as a one-time project or monthly **Website-as-a-Service (WaaS)** in USD and KES.';
        fallbackAction = { type: 'packages', label: 'Go to Packages Page' };
      } else if (lower.includes('contact') || lower.includes('call') || lower.includes('book') || lower.includes('email') || lower.includes('phone')) {
        fallbackText =
          'Our engineering team is available at **info@xweba.com** and WhatsApp (**+254 725 724 175**), or you can send a message directly on our Contact page.';
        fallbackAction = { type: 'callback', label: 'Go to Contact Page' };
      }

      setMessages((prev) => [
        ...prev,
        {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: fallbackText,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          recommendedAction: fallbackAction,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const renderFormattedContent = (raw: string) => {
    const lines = raw.split('\n');
    return lines.map((line, lineIdx) => {
      const trimmed = line.trim();
      if (!trimmed) {
        return <div key={lineIdx} className="h-1.5" />;
      }
      const isBullet = trimmed.startsWith('• ') || trimmed.startsWith('- ') || trimmed.startsWith('* ');
      const cleanLine = isBullet ? trimmed.slice(2) : line;

      const parts = cleanLine.split(/(\*\*[^*]+\*\*)/g);
      const renderedParts = parts.map((part, partIdx) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return (
            <strong key={partIdx} className="font-semibold text-inherit">
              {part.slice(2, -2)}
            </strong>
          );
        }
        return <React.Fragment key={partIdx}>{part}</React.Fragment>;
      });

      if (isBullet) {
        return (
          <div key={lineIdx} className="flex items-start gap-1.5 pl-1 mt-1">
            <span className="text-[#F26522] font-bold select-none">·</span>
            <span className="flex-1">{renderedParts}</span>
          </div>
        );
      }

      return (
        <p key={lineIdx} className={lineIdx > 0 ? 'mt-1.5' : ''}>
          {renderedParts}
        </p>
      );
    });
  };

  const lastMessage = messages[messages.length - 1];
  const quickReplies =
    messages.length === 1 && lastMessage?.suggestedFollowUps
      ? lastMessage.suggestedFollowUps.slice(0, 3)
      : [];

  return (
    <div className="fixed bottom-4 right-4 z-40">
      <AnimatePresence>
        {/* Simple Floating Trigger Button */}
        {!isOpen && (
          <motion.button
            key="chat-trigger"
            type="button"
            initial={{ opacity: 0, scale: 0.85, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 10 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            onClick={() => setIsOpen(true)}
            className="flex items-center gap-2.5 bg-[#0B2D52] hover:bg-[#071E36] text-white px-4 py-3 rounded-full shadow-lg border border-white/15 cursor-pointer"
            aria-label="Open Xweba Chat"
          >
            <span className="w-2 h-2 rounded-full bg-emerald-400" />
            <MessageSquare className="w-4 h-4 text-[#F26522]" />
            <span className="text-xs font-bold tracking-tight">Chat with Xweba</span>
          </motion.button>
        )}

        {/* Simple, Uncluttered Chat Window */}
        {isOpen && (
          <motion.div
            key="chat-window"
            initial={{ opacity: 0, scale: 0.92, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 15 }}
            transition={{ type: 'spring', stiffness: 350, damping: 26 }}
            className="w-[calc(100vw-2rem)] sm:w-[360px] h-[min(480px,calc(100dvh-5rem))] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden origin-bottom-right"
          >
          {/* Minimal Header */}
          <div className="bg-[#0B2D52] px-4 py-3 text-white flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
              <div className="min-w-0">
                <h4 className="text-xs font-bold tracking-tight truncate">Xweba Assistant</h4>
                {visitor && (
                  <button
                    type="button"
                    onClick={handleEditVisitorInfo}
                    className="text-[10px] text-slate-300 hover:text-white truncate block cursor-pointer"
                    title="Update contact info"
                  >
                    {visitor.name} · Change
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1 shrink-0">
              {visitor && messages.length > 1 && (
                <button
                  type="button"
                  onClick={handleResetChat}
                  title="Clear chat"
                  className="p-1.5 text-slate-300 hover:text-white rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
                  aria-label="Clear chat"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="p-1.5 text-slate-300 hover:text-white rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
                aria-label="Close chat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Pre-Chat Intake (Name, Email, Optional Phone) OR Active Chat Stream */}
          {!visitor ? (
            <div className="flex-1 p-4 overflow-y-auto bg-slate-50 flex flex-col justify-center">
              <div className="bg-white rounded-xl border border-slate-200/90 p-4 shadow-xs">
                <p className="text-xs font-bold text-slate-900 mb-1">
                  Start a conversation
                </p>
                <p className="text-[11px] text-slate-500 mb-3.5 leading-relaxed">
                  Please share your name and email before chatting so we can assist you better.
                </p>

                <form onSubmit={handleStartChatSubmit} className="space-y-2.5">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                      Name <span className="text-[#F26522]">*</span>
                    </label>
                    <div className="relative">
                      <User className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        type="text"
                        required
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        placeholder="Your name"
                        className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-[#F26522] focus:bg-white transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                      Email <span className="text-[#F26522]">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        type="email"
                        required
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        placeholder="you@company.com"
                        className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-[#F26522] focus:bg-white transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                      Phone Number <span className="text-slate-400 font-normal">(optional)</span>
                    </label>
                    <div className="relative">
                      <Phone className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        type="tel"
                        value={phoneInput}
                        onChange={(e) => setPhoneInput(e.target.value)}
                        placeholder="+254 700 000 000"
                        className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-[#F26522] focus:bg-white transition-all"
                      />
                    </div>
                  </div>

                  {formError && (
                    <p className="text-[11px] text-rose-600 font-medium">{formError}</p>
                  )}

                  <button
                    type="submit"
                    className="w-full py-2.5 px-4 bg-[#F26522] hover:bg-[#ea580c] text-white font-bold text-xs rounded-lg shadow-xs transition-colors cursor-pointer mt-1"
                  >
                    Start Chat
                  </button>
                </form>
              </div>
            </div>
          ) : (
            <div className="flex-1 p-3.5 overflow-y-auto space-y-3 bg-slate-50 text-xs">
              {messages.map((m) => {
                const isAssistant = m.role === 'assistant';
                return (
                  <div
                    key={m.id}
                    className={`flex flex-col ${isAssistant ? 'items-start' : 'items-end'}`}
                  >
                    <div
                      className={`max-w-[86%] rounded-2xl px-3.5 py-2.5 leading-relaxed ${
                        isAssistant
                          ? 'bg-white text-slate-800 border border-slate-200/90 rounded-tl-xs'
                          : 'bg-[#0B2D52] text-white rounded-tr-xs'
                      }`}
                    >
                      <div className="text-xs leading-relaxed">
                        {renderFormattedContent(m.content)}
                      </div>

                      {isAssistant && m.recommendedAction && (
                        <div className="mt-2 pt-2 border-t border-slate-100">
                          <button
                            type="button"
                            onClick={() => handleActionClick(m.recommendedAction!)}
                            className="inline-flex items-center gap-1 text-[11px] font-bold text-[#F26522] hover:text-[#0B2D52] transition-colors cursor-pointer"
                          >
                            <span>{m.recommendedAction.label}</span>
                            <ArrowUpRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {quickReplies.length > 0 && !isLoading && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {quickReplies.map((qp, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => handleSendMessage(qp)}
                      className="px-2.5 py-1.5 bg-white hover:bg-slate-100 text-slate-700 rounded-lg text-[11px] border border-slate-200 transition-colors cursor-pointer"
                    >
                      {qp}
                    </button>
                  ))}
                </div>
              )}

              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-slate-200 text-slate-500 px-3 py-2 rounded-2xl rounded-tl-xs flex items-center gap-2 text-xs">
                    <RotateCw className="w-3.5 h-3.5 animate-spin text-[#F26522]" />
                    <span>Typing...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Direct Links to Relevant Pages */}
          <div className="px-3 py-2 bg-slate-100/80 border-t border-slate-200/80 flex items-center justify-between gap-1 text-[11px] shrink-0">
            <a
              href="/xwebaudit"
              onClick={(e) => {
                e.preventDefault();
                navigateWithFallback('/xwebaudit', () => onOpenAudit?.());
              }}
              className="text-slate-600 hover:text-[#F26522] font-semibold inline-flex items-center gap-0.5 transition-colors"
            >
              <span>Free Audit</span>
              <ArrowUpRight className="w-3 h-3" />
            </a>
            <span className="text-slate-300">·</span>
            <a
              href="/packages"
              onClick={(e) => {
                e.preventDefault();
                navigateWithFallback('/packages', onNavigateToPackages);
              }}
              className="text-slate-600 hover:text-[#F26522] font-semibold inline-flex items-center gap-0.5 transition-colors"
            >
              <span>Packages</span>
              <ArrowUpRight className="w-3 h-3" />
            </a>
            <span className="text-slate-300">·</span>
            <a
              href="/services"
              onClick={(e) => {
                e.preventDefault();
                navigateWithFallback('/services', onNavigateToServices);
              }}
              className="text-slate-600 hover:text-[#F26522] font-semibold inline-flex items-center gap-0.5 transition-colors"
            >
              <span>Services</span>
              <ArrowUpRight className="w-3 h-3" />
            </a>
            <span className="text-slate-300">·</span>
            <a
              href="/contact"
              onClick={(e) => {
                e.preventDefault();
                navigateWithFallback('/contact', onNavigateToContact);
              }}
              className="text-slate-600 hover:text-[#F26522] font-semibold inline-flex items-center gap-0.5 transition-colors"
            >
              <span>Contact</span>
              <ArrowUpRight className="w-3 h-3" />
            </a>
          </div>

          {/* Clean Chat Input (only shown once visitor info is entered) */}
          {visitor && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage();
              }}
              className="p-2.5 bg-white border-t border-slate-200 flex items-center gap-2 shrink-0"
            >
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Write a message..."
                className="flex-1 min-w-0 px-3 py-2 min-h-[38px] bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-[#F26522] focus:bg-white transition-all"
              />
              <button
                type="submit"
                disabled={!input.trim() || isLoading}
                className="p-2 min-w-[38px] min-h-[38px] flex items-center justify-center bg-[#F26522] hover:bg-[#ea580c] disabled:opacity-50 text-white rounded-xl transition-colors cursor-pointer shrink-0"
                aria-label="Send Message"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </form>
          )}
        </motion.div>
      )}
    </AnimatePresence>
    </div>
  );
};
